import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import { validationRules, formatPhoneNumber, formatEmail } from '../utils/formValidation';
import { getEmailRecipient, addEnvironmentInfo } from '../utils/emailRouting';
import { getVWOFMEUserId } from '../utils/vwoApplyTest';
import { markFormSubmitted } from '../utils/interstitialFormTracker';

// Add CSS animations for mobile optimizations
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    /* Prevent zoom on input focus for mobile */
    @media screen and (-webkit-min-device-pixel-ratio:0) {
      select, textarea, input[type="text"], input[type="password"], 
      input[type="datetime"], input[type="datetime-local"], 
      input[type="date"], input[type="month"], input[type="time"], 
      input[type="week"], input[type="number"], input[type="email"], 
      input[type="url"], input[type="search"], input[type="tel"], 
      input[type="color"] {
        font-size: 16px !important;
      }
    }
    
    /* Better focus states for mobile */
    .mobile-form-input:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(124, 179, 66, 0.3);
      border-color: #7cb342;
    }
    
    /* Smooth animations for button interactions */
    .mobile-form-button {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .mobile-form-button:active {
      transform: scale(0.98);
    }
  `;
  document.head.appendChild(style);
}

/**
 * Map floorplan beds count to apartment type dropdown value
 * @param {string|number} bedsCount - The number of bedrooms from Contentful
 * @returns {string} - The apartment type value for the dropdown
 */
const getApartmentTypeFromBeds = (bedsCount) => {
  if (!bedsCount && bedsCount !== 0) return "";
  
  const beds = parseInt(bedsCount, 10);
  
  switch (beds) {
    case 0:
      return 'studio';
    case 1:
      return '1br';
    case 2:
      return '2br';
    case 3:
      return '3br';
    case 4:
      return '4br';
    default:
      return "";
  }
};

// Step carousel component
const StepCarousel = () => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      id: 1,
      title: "STEP ONE",
      subtitle: "When are you looking to Move?",
      icon: "mckinley", // McKinley logo
      color: "#4e6f92"
    },
    {
      id: 2,
      title: "STEP TWO", 
      subtitle: "Complete an application.\nIt will not impact your credit.",
      icon: "findigs", // Findigs logo
      color: "#7cb342"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleDotClick = (index) => {
    setCurrentStep(index);
  };

  return (
    <div style={{ 
      background: 'white',
      borderRadius: '12px',
      marginBottom: '24px',
      color: '#2c3e50',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
      minHeight: '140px',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #e0e0e0'
    }}>
      {/* Colored header bar */}
      <div style={{
        background: steps[currentStep].color,
        color: 'white',
        fontSize: '14px',
        fontWeight: '600',
        letterSpacing: '1px',
        padding: '12px 24px',
        transition: 'background-color 0.3s ease'
      }}>
        {steps[currentStep].title}
      </div>
      
      {/* White content area */}
      <div style={{
        padding: '24px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '100px' // Fixed minimum height for content area
      }}>
        {/* Icon container with fixed height */}
        <div style={{ 
          height: '32px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginBottom: '12px'
        }}>
          {steps[currentStep].icon === 'mckinley' && (
            <div style={{ fontSize: '24px' }}>
              🏢
            </div>
          )}
          {steps[currentStep].icon === 'findigs' && (
            <svg width="80" height="24" viewBox="0 0 134 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M122.2 27.5457L118.447 29.5864C118.912 30.7915 119.826 31.7286 121.187 32.3988C122.55 33.0678 124.215 33.4029 126.187 33.4029C128.534 33.4029 130.344 32.9284 131.619 31.9805C132.891 31.0326 133.528 29.6891 133.528 27.9478C133.528 26.4541 133.008 25.2997 131.966 24.4848C130.925 23.6698 129.309 23.1629 127.118 22.9619C125.523 22.8062 124.433 22.6052 123.846 22.3598C123.259 22.1145 122.966 21.7351 122.966 21.2206C122.966 20.7061 123.192 20.33 123.646 20.0836C124.101 19.8382 124.816 19.715 125.79 19.715C126.652 19.715 127.388 19.9106 127.998 20.3008C128.607 20.691 129.022 21.2325 129.244 21.9232L133.295 20.5851C132.852 19.2243 131.951 18.1543 130.59 17.3728C129.227 16.5935 127.605 16.2012 125.723 16.2012C123.487 16.2012 121.72 16.6876 120.426 17.6582C119.131 18.6288 118.483 19.9398 118.483 21.5892C118.483 23.1067 119.042 24.2783 120.161 25.103C121.278 25.9288 122.978 26.4314 125.259 26.6087C126.277 26.6984 127.053 26.8043 127.584 26.9264C128.116 27.0496 128.491 27.2172 128.712 27.4279C128.933 27.6409 129.045 27.9359 129.045 28.3153C129.045 29.3648 128.116 29.889 126.255 29.889C125.192 29.889 124.318 29.688 123.631 29.2859C122.944 28.8849 122.468 28.3056 122.202 27.5468" fill="#212B14"/>
              <path d="M112.635 16.5362L112.302 18.6785H112.201C111.094 17.0269 109.423 16.2012 107.187 16.2012C105.659 16.2012 104.307 16.5589 103.134 17.2723C101.96 17.9867 101.052 18.9908 100.41 20.2835C99.7671 21.5784 99.4463 23.084 99.4463 24.8015C99.4463 26.5189 99.7671 28.0256 100.41 29.3194C101.052 30.6143 101.96 31.6173 103.134 32.3307C104.306 33.0451 105.658 33.4018 107.187 33.4018C108.227 33.4018 109.163 33.1834 109.993 32.7479C110.824 32.3134 111.526 31.6724 112.103 30.824H112.201V32.5306C112.201 33.712 111.814 34.6502 111.038 35.3408C110.264 36.0326 109.202 36.3784 107.851 36.3784C105.636 36.3784 104.152 35.6305 103.399 34.1357L99.9441 36.5795C100.652 37.7392 101.649 38.5974 102.933 39.1551C104.218 39.7128 105.834 39.9917 107.782 39.9917C110.55 39.9917 112.714 39.3443 114.276 38.0516C115.838 36.7567 116.618 34.9604 116.618 32.6636V16.5341H112.633L112.635 16.5362ZM111.057 28.4655C110.337 29.3475 109.336 29.7874 108.05 29.7874C106.833 29.7874 105.869 29.341 105.161 28.4493C104.452 27.5566 104.098 26.3417 104.098 24.8026C104.098 23.2634 104.452 22.0475 105.161 21.1558C105.869 20.263 106.833 19.8177 108.05 19.8177C109.336 19.8177 110.337 20.2587 111.057 21.1396C111.776 22.0215 112.137 23.2418 112.137 24.8036C112.137 26.3655 111.776 27.5868 111.057 28.4677V28.4655Z" fill="#212B14"/>
              <path d="M95.3628 14.7959C96.0924 14.7959 96.691 14.5505 97.1566 14.0587C97.6212 13.568 97.854 12.9433 97.854 12.1845C97.854 11.4258 97.633 10.7913 97.1899 10.3438C96.7468 9.89854 96.1385 9.6748 95.3628 9.6748C94.5872 9.6748 93.9553 9.90935 93.4693 10.3774C92.9822 10.8454 92.7397 11.4485 92.7397 12.1845C92.7397 12.9206 92.9929 13.5399 93.5036 14.0414C94.0121 14.5429 94.6322 14.7948 95.3639 14.7948" fill="#212B14"/>
              <path d="M52.5817 14.7959C53.3134 14.7959 53.9099 14.5505 54.3766 14.0587C54.8411 13.568 55.0728 12.9433 55.0728 12.1845C55.0728 11.4258 54.8518 10.7913 54.4087 10.3438C53.9657 9.89854 53.3574 9.6748 52.5817 9.6748C51.806 9.6748 51.1763 9.90935 50.6892 10.3774C50.2011 10.8454 49.9575 11.4485 49.9575 12.1845C49.9575 12.9206 50.2129 13.5399 50.7225 14.0414C51.231 14.5429 51.8511 14.7948 52.5817 14.7948" fill="#212B14"/>
              <path d="M97.5558 16.5371H93.0713V33.0676H97.5558V16.5371Z" fill="#212B14"/>
              <path d="M39.6944 33.0679H44.1789V20.2847H50.2898V33.0679H54.7743V16.5364H44.1789V15.8349C44.1789 15.2318 44.3119 14.7919 44.578 14.513C44.843 14.2342 45.2861 14.0936 45.9062 14.0936C46.6583 14.0936 47.3223 14.1931 47.8995 14.3941L48.2321 10.5463C47.3009 10.301 46.2495 10.1777 45.0769 10.1777C43.5041 10.1777 42.2135 10.636 41.2071 11.5504C40.1987 12.4659 39.6955 13.6473 39.6955 15.0978V16.5364H37.0381V20.2847H39.6955V33.0679H39.6944Z" fill="#212B14"/>
              <path d="M85.9295 10.5127V18.6785H85.8287C85.2536 17.83 84.5724 17.2053 83.7871 16.8043C83.0007 16.4022 82.0641 16.2012 80.9794 16.2012C78.9206 16.2012 77.2266 16.8259 75.8985 18.0754C74.5703 19.3259 73.7732 21.0434 73.506 23.2289H73.4073C73.3183 20.9321 72.7261 19.1865 71.6307 17.9932C70.5342 16.8 68.9668 16.2023 66.9306 16.2023C65.8234 16.2023 64.8267 16.4152 63.9416 16.8389C63.0555 17.2626 62.3367 17.8754 61.7831 18.6796H61.6844L61.3185 16.5373H57.4316V33.0689H61.9161V24.3681C61.9161 22.9846 62.2487 21.88 62.9128 21.0542C63.5769 20.2295 64.463 19.8166 65.5702 19.8166C67.806 19.8166 68.925 21.2552 68.925 24.1335V33.0689H73.4095V26.4087H73.5082C73.7742 28.5737 74.5767 30.2803 75.9167 31.5298C77.2556 32.7792 78.9443 33.404 80.9816 33.404C81.9997 33.404 82.9084 33.1964 83.7055 32.7836C84.5026 32.3707 85.2332 31.7308 85.8984 30.8596H85.9971L86.4295 33.0689H90.4151V10.5127H85.9306H85.9295ZM84.8502 28.4656C84.1304 29.3475 83.1294 29.7874 81.8441 29.7874C80.6265 29.7874 79.6631 29.3411 78.955 28.4494C78.2458 27.5566 77.8918 26.3417 77.8918 24.8026C77.8918 23.2635 78.2458 22.0475 78.955 21.1558C79.6631 20.263 80.6265 19.8177 81.8441 19.8177C83.1294 19.8177 84.1304 20.2587 84.8502 21.1396C85.5701 22.0216 85.9306 23.2418 85.9306 24.8037C85.9306 26.3655 85.5701 27.5868 84.8502 28.4677V28.4656Z" fill="#212B14"/>
              <path d="M26.8607 13.9959C26.0765 13.9397 25.0766 13.7214 23.86 13.3409C22.6423 12.9594 21.6145 12.5584 20.7756 12.1358L19.6834 14.1224C20.2767 14.4131 20.9708 14.6941 21.7658 14.9665C19.073 16.1608 16.4617 17.647 13.933 19.4282C13.5714 19.6822 13.2195 19.9406 12.873 20.2C12.8537 19.8119 12.8333 19.4109 12.8119 19.0002C14.0263 18.0242 15.1421 16.9066 16.1581 15.6442C17.4723 14.0099 18.5011 12.2979 19.2425 10.508C19.9827 8.71812 20.355 7.03307 20.355 5.45504C20.355 3.76351 19.9355 2.43191 19.0955 1.45914C18.2565 0.486381 17.1236 0 15.6957 0C13.7088 0 12.1832 0.908993 11.1211 2.72806C10.0579 4.54604 9.49786 7.11846 9.44208 10.4453C9.41418 11.7693 9.4689 13.6446 9.60944 16.0679C9.64591 16.5802 9.68883 17.2309 9.73603 18.0166C9.25111 18.3214 8.80373 18.5668 8.39284 18.7527C7.55387 19.1342 6.68595 19.3245 5.7912 19.3245C4.89645 19.3245 4.13258 19.0067 3.58758 18.3722C3.0415 17.7378 2.71322 16.8861 2.60164 15.815L0 17.1249C0.111575 18.5917 0.707002 19.7892 1.78306 20.7188C2.86019 21.6505 4.16906 22.1141 5.70751 22.1141C6.6323 22.1141 7.58177 21.9315 8.56127 21.564C8.98934 21.404 9.43135 21.2019 9.88623 20.9609C9.90983 21.5359 9.93236 22.0828 9.95167 22.6038C8.3982 24.0154 7.03998 25.4939 5.87595 27.0396C3.80429 29.7882 2.769 32.4308 2.769 34.9687C2.769 36.5186 3.17454 37.7443 3.98668 38.6468C4.79775 39.5482 5.87488 40 7.21808 40C8.89707 40 10.2392 39.3861 11.2477 38.1604C12.254 36.9347 12.8419 35.1373 13.0103 32.7691C13.1208 31.3597 13.1713 29.7536 13.1573 27.9496C13.1487 26.8764 13.1037 25.3588 13.0211 23.3982C13.8171 22.741 14.6668 22.1012 15.5701 21.4797C17.9046 19.8735 20.3389 18.4868 22.873 17.3152C22.549 18.0534 22.2818 18.7516 22.0759 19.4077L24.3009 20.0422C24.553 19.1969 24.9371 18.209 25.4553 17.0828C25.9724 15.9555 26.497 15.067 27.0281 14.4185L26.8607 13.9959ZM12.5479 10.4864C12.5758 8.03394 12.8634 6.13921 13.4094 4.80004C13.9544 3.46195 14.7033 2.79075 15.6549 2.79075C16.1849 2.79075 16.5926 3.01665 16.8715 3.46736C17.1504 3.91915 17.291 4.60873 17.291 5.53826C17.291 6.83528 17.0324 8.20255 16.5142 9.639C15.995 11.0776 15.2901 12.4297 14.3954 13.6987C13.8557 14.4617 13.2753 15.147 12.6552 15.7577L12.6316 15.2626C12.5479 13.0361 12.5201 11.4429 12.5479 10.4842V10.4864ZM9.94631 32.7281C9.80577 34.1083 9.50645 35.201 9.04405 36.0041C8.58273 36.8083 8.00125 37.2093 7.30176 37.2093C6.85331 37.2093 6.49713 37.0266 6.23213 36.6602C5.96607 36.2927 5.83304 35.7285 5.83304 34.9687C5.83304 32.7129 6.72886 30.4021 8.51943 28.0339C8.99685 27.4027 9.5161 26.7845 10.0729 26.1781C10.0911 26.8439 10.1051 27.4276 10.1126 27.9291C10.1405 29.719 10.0847 31.3186 9.94524 32.7281H9.94631Z" fill="#8EDE65"/>
            </svg>
          )}
        </div>
        
        {/* Text container with fixed height */}
        <div style={{
          fontSize: '18px',
          fontWeight: '600',
          lineHeight: '1.3',
          whiteSpace: 'pre-line',
          height: '52px', // Fixed height to accommodate 2 lines
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center'
        }}>
          {steps[currentStep].subtitle}
        </div>

        {/* Progress dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          marginTop: '16px'
        }}>
          {steps.map((_, index) => (
            <div
              key={index}
              onClick={() => handleDotClick(index)}
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: currentStep === index ? steps[currentStep].color : 'rgba(44, 62, 80, 0.2)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                padding: '6px', // Increase clickable area
                margin: '-6px' // Compensate for padding
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const ApplyInterstitialFormRHF = ({ 
  communityName, 
  slug, 
  onSubmit, 
  onSkip,
  onClose = null,
  arrivalInfo = null,
  sessionId = null,
  buttonPosition = 'unknown',
  floorplanName = null,
  floorplanBeds = null,
  toEmail = null,
  trackEventFunction = null // VWO FME tracking function passed from parent
}) => {
  const [response, setResponse] = useState('');
  const [notification, setNotification] = useState('');
  const [mailpass, setMailpass] = useState('');
  const [mailuser, setMailuser] = useState('');
  const [sending, setSending] = useState(false);
  const [hasBeenSent, setHasBeenSent] = useState(false);

  // Get pre-populated apartment type from beds count (preferred) or floorplan name (fallback)
  const prePopulatedApartmentType = getApartmentTypeFromBeds(floorplanBeds);
  
  // Debug logging (development only)
  if (process.env.NODE_ENV === 'development') {
    console.log('🏠 Floorplan Debug:', {
      floorplanName,
      floorplanBeds,
      prePopulatedApartmentType,
      communityName,
      buttonPosition
    });
  }

  // Get consistent FME user ID for VWO tracking (with SSR protection)
  const userId = typeof window !== 'undefined' ? getVWOFMEUserId() : 'ssr-fallback-id';
  
  // Helper function to track events using passed tracking function
  const trackApplyEvent = (eventName, additionalData = {}) => {
    if (trackEventFunction && typeof trackEventFunction === 'function') {
      trackEventFunction(eventName, userId, slug, buttonPosition, additionalData);
    } else {
      // Fallback logging if no tracking function provided
      console.warn('🎯 VWO FME: trackEventFunction not provided to ApplyInterstitialFormRHF');
    }
  };

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset
  } = useForm({
    defaultValues: {
      fname: "",
      lname: "",
      email: "",
      phone: "",
      apartmentType: prePopulatedApartmentType,
      moveInDate: "",
      agreeToTextMessages: false
    },
    mode: "onChange"
  });

  // Watch phone and email fields for formatting
  const phoneValue = watch("phone");
  const emailValue = watch("email");

  // Format phone number on change
  useEffect(() => {
    if (phoneValue) {
      const formatted = formatPhoneNumber(phoneValue);
      if (formatted !== phoneValue) {
        setValue("phone", formatted);
      }
    }
  }, [phoneValue, setValue]);

  // Format email on change
  useEffect(() => {
    if (emailValue) {
      const formatted = formatEmail(emailValue);
      if (formatted !== emailValue) {
        setValue("email", formatted);
      }
    }
  }, [emailValue, setValue]);

  useEffect(() => {
    let mailpass = sessionStorage.getItem("mailpass");
    let mailuser = sessionStorage.getItem("mailuser");
    if (mailpass && mailpass.length > 0 && mailuser && mailuser.length > 0) {
      setMailpass(mailpass);
      setMailuser(mailuser);
    } else {
      getAuth('https://mailut.mcklab.com/?action=login&username=orangepup&password=' + createPass());
    }
  }, []);

  const createPass = () => {
    let date = new Date();
    let dateString = date.toISOString().slice(0, 10);
    let pass = 'froglegs';
    let encodedString = btoa(dateString + pass);
    return encodedString;
  };

  const getAuth = (url) => {
    fetch(url).then(response => response.json()).then(res => {
      if (res.message_type == 'success') {
        setMailpass(res.loggedin_pass);
        setMailuser(res.loggedin_user);
        sessionStorage.setItem("mailpass", res.loggedin_pass);
        sessionStorage.setItem("mailuser", res.loggedin_user);
      } else {
        setResponse('We were not able to authorize this form to send emails. Please try again later');
        setNotification('is-warning');
      }
    });
  };

  const sendMail = (url, data) => {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'tenOrangePups22'
      },
      body: JSON.stringify(data)
    }).then(response => response.json()).then(res => {
      if (res.message_type == 'success') {
        setResponse('Information captured! Redirecting to application...');
        setNotification('is-success');
        reset(); // Reset form on success
        
        // Track conversion with position
        trackApplyEvent('form_submitted');
        
        // Mark form as submitted for this property to prevent future displays
        markFormSubmitted(slug);
        
        // Redirect after short delay
        setTimeout(() => {
          onSubmit();
        }, 2000);
      } else {
        setResponse('Form submission failed, but you can still continue to apply');
        setNotification('is-warning');
        
        // Track form error
        trackApplyEvent('form_error', {
          error_type: 'email_failure',
          error_message: 'Email submission failed'
        });
        
        // Still allow redirect even if email fails
        setTimeout(() => {
          onSubmit();
        }, 2000);
      }
      setSending(false);
      setHasBeenSent(true);
    }).catch(error => {
      console.error('Form submission error:', error);
      setResponse('Form submission failed, but you can still continue to apply');
      setNotification('is-warning');
      
      // Track form error
      trackApplyEvent('form_error', {
        error_type: 'network_error',
        error_message: error.message
      });
      
      setSending(false);
      setHasBeenSent(true);
      
      // Still allow redirect
      setTimeout(() => {
        onSubmit();
      }, 2000);
    });
  };

  const handleFormSubmit = (data, event) => {
    // Prevent all form submission behaviors
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    setSending(true);

    // Use environment-based email routing with UTM source
    const recipientEmail = getEmailRecipient(toEmail, slug, arrivalInfo?.utmSource);

    // Prepare base email body
    const baseBody = `Pre-Application Lead Capture

Property: ${communityName}
First Name: ${data.fname.trim()} 
Last Name: ${data.lname.trim()}
Email: ${data.email.trim()} 
Phone: ${data.phone.replace(/\D/g, "")} 
Apartment Type: ${data.apartmentType.trim() || 'Not specified'}
Move-in Date: ${data.moveInDate.trim() || 'Not specified'}
Agreed to Text Messages: ${data.agreeToTextMessages ? 'Yes' : 'No'}

-------------------------------

Apply Button Location: ${buttonPosition || 'unknown'}
`;

    // Add environment and tracking info
    const fullBody = addEnvironmentInfo(baseBody, {
      utmSource: arrivalInfo?.utmSource,
      utmMedium: arrivalInfo?.utmMedium,
      utmCampaign: arrivalInfo?.utmCampaign,
      referrer: arrivalInfo?.referrer,
      sessionId: sessionId
    });

    let sendData = {
      'loggedin_pass': mailpass,
      'loggedin_user': mailuser,
      'action': 'mail',
      'to': recipientEmail,
      'name': data.fname.trim() + ' ' + data.lname.trim(),
      'subject': `Pre-Application Lead Capture - ${communityName} - ${data.fname.trim()} ${data.lname.trim()}`,
      'body': fullBody
    };

    let endpoint = 'https://mailut.mcklab.com';
    sendMail(endpoint, sendData);
  };

  const handleSkip = () => {
    // Track skip event with position
    trackApplyEvent('form_skipped');
    onSkip();
  };
  
  const handleClose = (event) => {
    // Prevent any default behavior and stop propagation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Use onClose if provided, otherwise fall back to onSkip
    if (onClose) {
      onClose();
    } else {
      handleSkip();
    }
  };

  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483799, // Set to maximum z-index to render above all elements
        padding: window.innerWidth <= 768 ? '0' : '20px' // Full width on mobile
      }}
      onClick={handleClose}
    >
      <div 
        style={{
          backgroundColor: '#2c3e50',
          borderRadius: window.innerWidth <= 768 ? '0' : '16px', // No border radius on mobile for full width
          padding: window.innerWidth <= 768 ? '20px' : '32px',
          maxWidth: window.innerWidth <= 768 ? 'none' : '560px', // Full width on mobile
          width: '100%',
          height: window.innerWidth <= 768 ? '100vh' : 'auto', // Full height on mobile
          maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
          overflowY: 'auto',
          position: 'relative',
          color: 'white'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button - more visible on mobile */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: window.innerWidth <= 768 ? '20px' : '16px',
            right: window.innerWidth <= 768 ? '20px' : '16px',
            background: window.innerWidth <= 768 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.1)', // More visible on mobile
            border: 'none',
            borderRadius: '50%',
            width: window.innerWidth <= 768 ? '40px' : '32px', // Larger on mobile
            height: window.innerWidth <= 768 ? '40px' : '32px', // Larger on mobile
            color: window.innerWidth <= 768 ? '#2c3e50' : 'white', // Dark text on light background for mobile
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: window.innerWidth <= 768 ? '24px' : '18px', // Larger font on mobile
            fontWeight: 'bold',
            boxShadow: window.innerWidth <= 768 ? '0 2px 8px rgba(0, 0, 0, 0.3)' : 'none', // Shadow for visibility on mobile
            zIndex: 1 // Ensure it's above other content
          }}
        >
          ×
        </button>

        {/* Step Carousel */}
        <StepCarousel />

        {response && (
          <div style={{
            backgroundColor: notification.includes('success') ? '#27ae60' : '#f39c12',
            color: 'white',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            {response}
          </div>
        )}

        <form onSubmit={handleSubmit(handleFormSubmit)} onReset={(e) => e.preventDefault()}>
          {/* First and Last Name Row */}
          <div style={{ 
            display: 'flex', 
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
            gap: window.innerWidth <= 768 ? '12px' : '16px', 
            marginBottom: window.innerWidth <= 768 ? '16px' : '20px' 
          }}>
            <div style={{ flex: 1 }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'left'
              }}>
                First Name*
              </label>
              <input
                {...register("fname", validationRules.firstName)}
                className="mobile-form-input"
                style={{
                  width: '100%',
                  padding: window.innerWidth <= 768 ? '10px' : '12px',
                  borderRadius: '8px',
                  border: errors.fname ? '2px solid #e74c3c' : '1px solid #34495e',
                  backgroundColor: 'white',
                  color: '#2c3e50',
                  fontSize: '16px'
                }}
                type="text"
                autoComplete="given-name"
                placeholder="First name"
                autoFocus
              />
              {errors.fname && (
                <p style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                  {errors.fname.message}
                </p>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'left'
              }}>
                Last Name*
              </label>
              <input
                {...register("lname", validationRules.lastName)}
                style={{
                  width: '100%',
                  padding: window.innerWidth <= 768 ? '10px' : '12px',
                  borderRadius: '8px',
                  border: errors.lname ? '2px solid #e74c3c' : '1px solid #34495e',
                  backgroundColor: 'white',
                  color: '#2c3e50',
                  fontSize: '16px'
                }}
                type="text"
                autoComplete="family-name"
                placeholder="Last name"
              />
              {errors.lname && (
                <p style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                  {errors.lname.message}
                </p>
              )}
            </div>
          </div>

          {/* Email Address */}
          <div style={{ marginBottom: window.innerWidth <= 768 ? '16px' : '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: 'left'
            }}>
              Email Address*
            </label>
            <input
              {...register("email", validationRules.email)}
              style={{
                width: '100%',
                padding: window.innerWidth <= 768 ? '10px' : '12px',
                borderRadius: '8px',
                border: errors.email ? '2px solid #e74c3c' : '1px solid #34495e',
                backgroundColor: 'white',
                color: '#2c3e50',
                fontSize: '16px'
              }}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="Email"
            />
            {errors.email && (
              <p style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Phone and Apartment Type Row */}
          <div style={{ 
            display: 'flex', 
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
            gap: window.innerWidth <= 768 ? '12px' : '16px', 
            marginBottom: window.innerWidth <= 768 ? '16px' : '20px' 
          }}>
            <div style={{ flex: 1 }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'left'
              }}>
                Phone Number*
              </label>
              <input
                {...register("phone", validationRules.phone)}
                style={{
                  width: '100%',
                  padding: window.innerWidth <= 768 ? '10px' : '12px',
                  borderRadius: '8px',
                  border: errors.phone ? '2px solid #e74c3c' : '1px solid #34495e',
                  backgroundColor: 'white',
                  color: '#2c3e50',
                  fontSize: '16px'
                }}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="Phone"
              />
              {errors.phone && (
                <p style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                  {errors.phone.message}
                </p>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'left'
              }}>
                Apartment Type*
              </label>
              <select
                {...register("apartmentType", { 
                  required: "Please select apartment type",
                  validate: value => value !== "" || "Please select apartment type"
                })}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: errors.apartmentType ? '2px solid #e74c3c' : '1px solid #34495e',
                  backgroundColor: 'white',
                  color: '#2c3e50',
                  fontSize: '16px'
                }}
              >
                <option value="">Select apartment type</option>
                <option value="studio">Studio</option>
                <option value="1br">1 Bedroom</option>
                <option value="2br">2 Bedroom</option>
                <option value="3br">3 Bedroom</option>
                <option value="4br">4 Bedroom</option>
              </select>
              {errors.apartmentType && (
                <p style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                  {errors.apartmentType.message}
                </p>
              )}
            </div>
          </div>

          {/* Move-in Date */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: 'left'
            }}>
              Move-in Date
            </label>
            <input
              {...register("moveInDate")}
              className="mobile-form-input"
              style={{
                width: '100%',
                padding: window.innerWidth <= 768 ? '10px' : '12px',
                borderRadius: '8px',
                border: errors.moveInDate ? '2px solid #e74c3c' : '1px solid #34495e',
                backgroundColor: 'white',
                color: '#2c3e50',
                fontSize: '16px'
              }}
              type="date"
              autoComplete="off"
            />
            {errors.moveInDate && (
              <p style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                {errors.moveInDate.message}
              </p>
            )}
          </div>

          {/* SMS Agreement Checkbox */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              lineHeight: '1.4'
            }}>
              <input
                {...register("agreeToTextMessages", validationRules.agreeToTextMessages)}
                type="checkbox"
                style={{
                  marginRight: '12px',
                  marginTop: '2px',
                  cursor: 'pointer'
                }}
              />
              <span>
                By checking this box you agree to our Privacy Policy and to receive text messages from McKinley. Reply STOP to opt out
              </span>
            </label>
            {errors.agreeToTextMessages && (
              <p style={{ color: '#e74c3c', fontSize: '12px', marginTop: '8px', marginLeft: '24px' }}>
                {errors.agreeToTextMessages.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div style={{ marginBottom: '16px' }}>
            {!sending && !hasBeenSent && (
              <button
                type="submit"
                className="mobile-form-button"
                onClick={(e) => {
                  // Additional event prevention at button level
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit(handleFormSubmit)(e);
                  
                  // Add haptic feedback on iOS
                  if (typeof window !== 'undefined' && window.navigator?.vibrate) {
                    window.navigator.vibrate(50);
                  }
                }}
                style={{
                  width: '100%',
                  padding: window.innerWidth <= 768 ? '14px 20px' : '16px',
                  backgroundColor: '#7cb342',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: window.innerWidth <= 768 ? '18px' : '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(124, 179, 66, 0.3)',
                  transform: 'translateY(0)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#6a9b37';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(124, 179, 66, 0.4)';
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = '#7cb342';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(124, 179, 66, 0.3)';
                  }
                }}
                disabled={isSubmitting}
              >
                Continue to Step 2 →
              </button>
            )}
            {sending && (
              <button 
                style={{
                  width: '100%',
                  padding: window.innerWidth <= 768 ? '14px 20px' : '16px',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: window.innerWidth <= 768 ? '18px' : '16px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                disabled
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Submitting...
              </button>
            )}
            {hasBeenSent && (
              <button 
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
                disabled
              >
                Redirecting...
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );

  // Use React Portal to render modal at document root level
  if (typeof document !== 'undefined') {
    return createPortal(modalContent, document.body);
  }
  
  // Fallback for SSR
  return modalContent;
};

export default ApplyInterstitialFormRHF;